import { useRef } from "react";
import { cn } from "@/lib/utils";
import { useFoilAnimation } from "@/hooks/useFoilAnimation";

const SIZES = {
  sm: { container: "h-7 w-7 rounded", svg: 14 },
  md: { container: "h-10 w-10 rounded-lg", svg: 20 },
  lg: { container: "h-12 w-12 rounded-lg", svg: 22 },
  xl: { container: "h-14 w-14 rounded-lg", svg: 26 },
  xxl: { container: "h-20 w-20 rounded-2xl", svg: 38 },
} as const;

interface LogoIconProps {
  size?: keyof typeof SIZES;
  glow?: boolean;
  foil?: boolean;
  animateFoil?: boolean;
  foilFps?: number;
  bare?: boolean;
  stroke?: string;
  className?: string;
}

const GRADIENT =
  "linear-gradient(to bottom, var(--interactive-bg), var(--logo-gradient-end))";

export function LogoIcon({
  size = "md",
  glow = true,
  foil = false,
  animateFoil = true,
  foilFps,
  bare = false,
  stroke,
  className,
}: LogoIconProps) {
  const { container, svg } = SIZES[size];
  const foilRef = useRef<HTMLDivElement>(null);
  useFoilAnimation(foilRef, foil && animateFoil, foilFps);
  const resolvedStroke =
    stroke ?? (foil ? "rgba(255,255,255,0.9)" : "var(--interactive)");

  return (
    <div
      ref={foilRef}
      style={foil || bare ? undefined : { background: GRADIENT }}
      className={cn(
        "flex shrink-0 items-center justify-center",
        !bare && "border-border-interactive border",
        container,
        glow && !foil && !bare && "glow-interactive",
        foil && "logo-foil-idle",
        className,
      )}
    >
      <svg fill="none" height={svg} viewBox="0 0 24 24" width={svg}>
        <path
          d="M12 3L2 21h20L12 3z"
          fill="none"
          stroke={resolvedStroke}
          strokeLinejoin="round"
          strokeWidth="2.5"
        />
        <line
          stroke={resolvedStroke}
          strokeWidth="2.2"
          x1="6"
          x2="18"
          y1="15"
          y2="15"
        />
      </svg>
    </div>
  );
}

export { GRADIENT as LOGO_GRADIENT };
